import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Code2, Cpu, Globe, Zap, Mail, Github,
  Twitter, Instagram, Linkedin, ChevronRight, Send, MessageCircle, ChevronDown, MapPin,
  Cloud, Database, ShieldCheck, CheckCircle
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

const TelegramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.499 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.12-2.678-1.795-1.185-.779-.416-1.207.258-1.908.177-.182 3.246-2.977 3.305-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.141-5.061 3.345-.479.329-.912.491-1.3.483-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.14.121.099.155.232.169.326.014.094.028.27.014.37z" />
  </svg>
);

// --- Styled Components ---

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled.div`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const RouterNavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const LangContainer = styled.div`
  position: relative;
  margin-right: 15px;
`;

const LangButton = styled.button`
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: #0d1117;
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  min-width: 140px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LangOption = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-muted)'};
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  text-align: left;
  transition: all 0.2s;
  font-weight: ${props => props.active ? '600' : '400'};
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-main);
  }
`;

const ActionButton = styled(motion.button)`
  padding: 10px 24px;
  border-radius: 99px;
  border: none;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 20px -5px var(--primary-glow);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -5px var(--primary-glow);
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 120px 5% 60px;
  position: relative;
  overflow: hidden;
`;

const HeroBadge = styled(motion.div)`
  padding: 8px 16px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--primary);
  margin-bottom: 24px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.2rem, 8vw, 4.5rem);
  text-align: center;
  line-height: 1.1;
  max-width: 1000px;
  margin-bottom: 24px;
`;

const HeroSub = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: var(--text-muted);
  text-align: center;
  max-width: 700px;
  margin-bottom: 40px;
  line-height: 1.6;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled(motion.div)`
  padding: 40px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(17, 24, 39, 0.9);
    transform: translateY(-10px);
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  margin-bottom: 24px;
`;

const Blob = styled(motion.div)`
  position: absolute;
  filter: blur(90px);
  z-index: -1;
  opacity: 0.3;
  border-radius: 50%;
  pointer-events: none;
`;

const Section = styled.section`
  padding: 100px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  text-align: center;
  margin-bottom: 60px;
  
  span {
    display: block;
    font-size: 0.9rem;
    text-transform: uppercase;
    color: var(--primary);
    letter-spacing: 2px;
    margin-bottom: 10px;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const StatCard = styled.div`
  padding: 30px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  text-align: center;
  
  h4 {
    font-size: 2rem;
    color: var(--primary);
    margin-bottom: 5px;
  }
  p {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
`;

const MarqueeContainer = styled.div`
  overflow: hidden;
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 40px 0;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 250px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-dark), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-dark), transparent);
  }
`;

const MarqueeTrack = styled.div`
  display: flex;
  gap: 30px;
  width: max-content;
  animation: scrollPortfolio 50s linear infinite;
  padding: 0 15px;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scrollPortfolio {
    0% { transform: translateX(0); }
    100% { transform: translateX(calc(-50% - 15px)); }
  }
`;

const HorizontalProjectCard = styled(motion.div)`
  width: 400px;
  border-radius: 24px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  flex-shrink: 0;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-5px);
  }
`;

const ProjectCard = styled(motion.div)`
  border-radius: 24px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  position: relative;
`;

const ProjectImage = styled.div`
  height: 250px;
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, var(--bg-dark), transparent);
  }
`;

const ProjectInfo = styled.div`
  padding: 24px;
  h3 { margin-bottom: 8px; }
  p { color: var(--text-muted); font-size: 0.9rem; line-height: 1.5; }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const ReviewCard = styled(motion.div)`
  padding: 30px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 20px;
    right: 30px;
    font-size: 4rem;
    color: rgba(99, 102, 241, 0.1);
    font-family: serif;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled(motion.form)`
  padding: 40px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 50px rgba(0,0,0,0.3);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    padding: 1px;
    background: linear-gradient(to bottom right, rgba(255,255,255,0.1), rgba(255,255,255,0.02));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 18px 24px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  color: var(--text-main);
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--glass-bg);
    box-shadow: 0 0 0 1px var(--primary), 0 0 20px rgba(99, 102, 241, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 18px 24px;
  background: var(--input-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  color: var(--text-main);
  font-size: 1rem;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--glass-bg);
    box-shadow: 0 0 0 1px var(--primary), 0 0 20px rgba(99, 102, 241, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Glow = styled.div`
  position: absolute;
  width: 40vw;
  height: 40vw;
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
  top: -20vh;
  left: -10vw;
  filter: blur(100px);
  z-index: -1;
  opacity: 0.5;
`;

const GlowRight = styled(Glow)`
  top: 40vh;
  left: auto;
  right: -10vw;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, transparent 70%);
`;



// --- Components ---

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const formRef = useRef();
  const navigate = useNavigate();
  const [technologies, setTechnologies] = useState([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    fetch(`${process.env.REACT_APP_API_URL || ''}/api/technologies`)
      .then(res => res.json())
      .then(data => setTechnologies(Array.isArray(data) ? data : []))
      .catch(console.error);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const formData = new FormData(formRef.current);
    const data = {
      user_name: formData.get('user_name'),
      user_email: formData.get('user_email'),
      subject: formData.get('subject'),
      message: formData.get('message'),
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        formRef.current.reset();
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Home contact error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <>
      <Navbar />

      <HeroSection>

        <Blob
          animate={{
            x: [0, 50, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          style={{
            top: '-20%',
            left: '-10%',
            width: '50vw',
            height: '50vw',
            background: 'var(--primary)',
            opacity: 0.25
          }}
        />
        <Blob
          animate={{
            x: [0, -50, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          style={{
            bottom: '-10%',
            right: '-10%',
            width: '60vw',
            height: '60vw',
            background: 'var(--secondary)',
            opacity: 0.2
          }}
        />
        <HeroBadge
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {t('hero.badge')}
        </HeroBadge>
        <HeroTitle
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          {t('hero.title').split(' ').map((word, i) =>
            word === 'Digital' || word === 'Authority' || word === 'Autoridad' || word === 'Цифровое' || word === 'Превосходство' || word === 'Dijital' || word === 'Otorite' ?
              <span key={i} className="gradient-text">{word} </span> : word + ' '
          )}
        </HeroTitle>
        <HeroSub
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        >
          {t('hero.sub')}
        </HeroSub>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <ActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            {t('hero.cta')} <ArrowRight size={18} />
          </ActionButton>
          <button
            onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })}
            style={{
              padding: '10px 24px',
              borderRadius: '99px',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass-bg)',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('hero.cases')}
          </button>
        </motion.div>
      </HeroSection>

      <Section id="about" key={i18n.language}>
        <AboutContent>
          <div>
            <SectionTitle style={{ textAlign: 'left', marginBottom: '30px' }}>
              <span>{t('about.tag')}</span>
              {t('about.title')}
            </SectionTitle>
            <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', marginBottom: '30px', fontSize: '1.1rem' }}>
              {t('about.text')}
            </p>
            <ActionButton whileHover={{ scale: 1.05 }} onClick={() => navigate('/about')}>{t('about.learn')}</ActionButton>
          </div>
          <StatGrid>
            <StatCard>
              <h4>150+</h4>
              <p>{t('about.stats.projects')}</p>
            </StatCard>
            <StatCard>
              <h4>5+</h4>
              <p>{t('about.stats.years')}</p>
            </StatCard>
            <StatCard>
              <h4>100%</h4>
              <p>{t('about.stats.clients')}</p>
            </StatCard>
            <StatCard>
              <h4>24/7</h4>
              <p>{t('about.stats.support')}</p>
            </StatCard>
          </StatGrid>
        </AboutContent>
      </Section>

      <section id="services" style={{ padding: '80px 0', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <SectionTitle>
            <span>{t('services.tag')}</span>
            {t('services.title')}
          </SectionTitle>
        </div>
        <FeatureGrid
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          {['s1', 's2', 's3', 's4', 's5', 's6'].map((sKey) => (
            <FeatureCard
              key={sKey}
              variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/services')}
              style={{ cursor: 'pointer' }}
            >
              <IconBox>
                {sKey === 's1' && <Cpu size={24} />}
                {sKey === 's2' && <Globe size={24} />}
                {sKey === 's3' && <Zap size={24} />}
                {sKey === 's4' && <Cloud size={24} />}
                {sKey === 's5' && <Database size={24} />}
                {sKey === 's6' && <ShieldCheck size={24} />}
              </IconBox>
              <h3 style={{ marginBottom: '16px' }}>{t(`services.${sKey}.t`)}</h3>
              <p style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{t(`services.${sKey}.d`)}</p>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </section>

      <Section id="projects">
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <SectionTitle>
            <span>{t('projects.tag')}</span>
            {t('projects.title')}
          </SectionTitle>
        </div>

        <MarqueeContainer>
          <MarqueeTrack>
            {[...(t('projects.items', { returnObjects: true }) || []), ...(t('projects.items', { returnObjects: true }) || [])].map((project, i) => (
              <HorizontalProjectCard
                key={i}
                whileHover={{ scale: 1.02 }}
                onClick={() => navigate(`/projects/${project.slug}`)}
                style={{ cursor: 'pointer' }}
              >
                <ProjectImage img={project.img} />
                <ProjectInfo>
                  <div style={{ color: 'var(--primary)', fontSize: '0.8rem', marginBottom: '5px' }}>{project.category}</div>
                  <h3 style={{ fontSize: '1.2rem' }}>{project.title}</h3>
                  <p style={{ fontSize: '0.85rem' }}>{project.desc}</p>
                </ProjectInfo>
              </HorizontalProjectCard>
            ))}
          </MarqueeTrack>
        </MarqueeContainer>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <ActionButton whileHover={{ scale: 1.05 }} onClick={() => navigate('/projects')}>
            {t('hero.cases')} <ArrowRight size={18} />
          </ActionButton>
        </div>
      </Section>

      <section id="reviews" style={{ padding: '100px 5%', background: 'rgba(255,255,255,0.01)' }}>
        <SectionTitle>
          <span>{t('reviews.tag')}</span>
          {t('reviews.title')}
        </SectionTitle>
        <ReviewGrid
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.25
              }
            }
          }}
        >
          {[
            { name: 'Alex Thompson', role: 'CEO, TechFlow', text: i18n.language.startsWith('ru') ? 'Nexova Dev LLC трансформировали наш подход к разработке.' : 'Nexova Dev LLC transformed our development process.' },
            { name: 'Selin Yıldız', role: 'Product Lead, SmartRetail', text: i18n.language.startsWith('ru') ? 'Качество кода и внимание к деталям поражают.' : 'The code quality and attention to detail are amazing.' },
            { name: 'James Miller', role: 'Founder, CloudNode', text: i18n.language.startsWith('ru') ? 'Рекомендую для сложных масштабируемых систем.' : 'Highly recommend for complex scalable systems.' }
          ].map((review, i) => (
            <ReviewCard
              key={i}
              variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } } }}
            >
              <p style={{ lineHeight: '1.7', marginBottom: '20px', fontStyle: 'italic' }}>"{review.text}"</p>
              <div>
                <strong style={{ display: 'block' }}>{review.name}</strong>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{review.role}</span>
              </div>
            </ReviewCard>
          ))}
        </ReviewGrid>
      </section>

      <Section id="contact">
        <ContactGrid>
          <div>
            <SectionTitle style={{ textAlign: 'left', marginBottom: '30px' }}>
              <span>{t('contact.tag')}</span>
              {t('contact.title')}
            </SectionTitle>
            <p style={{ color: 'var(--text-muted)', marginBottom: '40px' }}>{t('contact.sub')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ color: 'var(--primary)' }}><Mail /></div>
                <span>contact@nexovadev.com</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ color: 'var(--primary)' }}><TelegramIcon /></div>
                <span>Telegram: @nexova_dev</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ color: 'var(--primary)' }}><MapPin /></div>
                <span>{t('contact.address')}</span>
              </div>
            </div>
          </div>
          <ContactForm
            ref={formRef}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
          >
            <div style={{ marginBottom: '5px' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{t('contact.form.title')}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('contact.form.subtitle')}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <Input name="user_name" placeholder={t('contact.form.name')} required />
              <Input name="user_email" placeholder={t('contact.form.email')} type="email" required />
            </div>
            <Input name="subject" placeholder={t('contact.form.subject')} required />
            <TextArea name="message" placeholder={t('contact.form.message')} required />

            <ActionButton
              type="submit"
              whileHover={{ scale: 1.02, boxShadow: '0 20px 40px -10px var(--primary-glow)' }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', justifyContent: 'center', marginTop: '10px', height: '54px', fontSize: '1.1rem' }}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : (status === 'success' ? 'Sent!' : t('contact.form.send'))}
              {status === 'sending' ? null : (status === 'success' ? <CheckCircle size={20} /> : <Send size={20} />)}
            </ActionButton>

            {status === 'success' && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#22c55e', textAlign: 'center', fontSize: '0.9rem', marginTop: '10px' }}>
                Message sent successfully!
              </motion.p>
            )}
            {status === 'error' && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#f43f5e', textAlign: 'center', fontSize: '0.9rem', marginTop: '10px' }}>
                Error sending message. Please try again.
              </motion.p>
            )}
          </ContactForm>
        </ContactGrid>
      </Section>

      <Footer />
    </>
  );
};

export default HomePage;
